import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity.
 *
 * This component follows a pure event-driven architecture where quantity changes
 * are broadcast via QuantitySelectorUpdateEvent. Parent components that contain
 * quantity selectors listen for these events and handle them according to their
 * specific needs, with event filtering ensuring each parent only processes events
 * from its own quantity selectors to prevent conflicts between different cart
 * update strategies.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    // Capture server-disabled state on first load
    const { minusButton, plusButton } = this.refs;

    if (!this.initialized && minusButton.disabled) {
      this.serverDisabledMinus = true;
    }
    if (!this.initialized && plusButton.disabled) {
      this.serverDisabledPlus = true;
    }

    this.initialized = true;
    this.normalizeValue();
    const form = this.closest('form');
    const { signal } = this.#abortController;
    form?.addEventListener('submit', () => this.normalizeValue(), { capture: true, signal });
    form?.addEventListener('click', this.#normalizePurchaseQuantity, { capture: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  /**
   * Checks if the current quantity can be added to cart without exceeding max
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
   */
  canAddToCart() {
    this.normalizeValue();
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !this.refs.quantityInput.disabled && !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets the current quantity value
   * @returns {string} The current value
   */
  getValue() {
    this.normalizeValue();
    return this.refs.quantityInput.value;
  }

  /**
   * Sets the current quantity value
   * @param {string} value - The value to set
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment
   * @param {string} min - Minimum value
   * @param {string|null} max - Maximum value (null if no max)
   * @param {string} step - Step increment
   */
  updateConstraints(min, max, step, cartQuantity) {
    const { quantityInput } = this.refs;

    quantityInput.min = min;
    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = step;
    if (cartQuantity != null) {
      quantityInput.setAttribute('data-cart-quantity', cartQuantity);
    }
    this.normalizeValue();
    this.onQuantityChange();
  }

  normalizeQuantity(value) {
    const { min, step } = this.getCurrentValues();
    const increment = Math.max(1, step);
    const maximum = this.getEffectiveMax();
    const validMaximum = maximum === null ? Infinity : min + Math.floor((maximum - min) / increment) * increment;
    const inputValue = Number(value);
    const usableValue = Number.isFinite(inputValue) && Math.abs(inputValue) <= Number.MAX_SAFE_INTEGER ? inputValue : min;
    const bounded = Math.max(min, Math.min(validMaximum, usableValue));
    const aligned = min + Math.round((bounded - min) / increment) * increment;
    return Math.max(min, Math.min(validMaximum, aligned));
  }

  normalizeValue() {
    const { quantityInput } = this.refs;
    const value = this.normalizeQuantity(quantityInput.value).toString();
    const changed = value !== quantityInput.value;
    quantityInput.value = value;
    this.updateButtonStates();
    if (changed) this.onQuantityChange();
    return value;
  }

  #normalizePurchaseQuantity = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[type="submit"], shopify-accelerated-checkout, .shopify-payment-button')) {
      this.normalizeValue();
    }
  };

  handleInput(event) {
    if (this.refs.quantityInput.dataset.cartLine || this.closest('quick-order-list')) return;
    if (!(event.target instanceof HTMLInputElement) || event.target.value === '') return;
    if (Number(event.target.value) === this.normalizeQuantity(event.target.value)) {
      this.onQuantityChange();
      this.updateButtonStates();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Enter') this.normalizeValue();
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  /**
   * Gets the effective maximum value for this quantity selector
   * Product page: max - cartQuantity (how many can be added)
   * Override in subclass for different behavior
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    // Product page: can only add what's left
    return Math.max(max - cartQuantity, min);
  }

  /**
   * Updates button states based on current value and limits
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const inputDisabled = this.refs.quantityInput.disabled;

    // Only manage buttons that weren't server-disabled
    if (!this.serverDisabledMinus) {
      minusButton.disabled = inputDisabled || value <= min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = inputDisabled || (effectiveMax !== null && this.normalizeQuantity(value + step) <= value);
    }
  }

  /**
   * Updates quantity by a given step
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    if (quantityInput.disabled) return;
    const { step, value } = this.getCurrentValues();
    const newValue = this.normalizeQuantity(value + step * stepMultiplier);

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    // Non-empty value so morph's PRESERVED_ATTRIBUTES keeps the marker across a section
    // morph (its onBeforeUpdate ignores empty-valued attributes). skipsValueUpdate only
    // checks presence, not the value.
    quantityInput.setAttribute('data-skip-value-update', 'true');
    quantityInput.select();
  }

  /**
   * Handles the quantity set event (on blur).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.type === 'change' && (this.refs.quantityInput.dataset.cartLine || this.closest('quick-order-list'))) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    // Blur always ends the edit, so the morph opt-out has to be cleared before any
    // validation branch can return early. Leaving it set would make morph's
    // PRESERVED_ATTRIBUTES copy it onto every incoming server node, freezing this
    // input's displayed value against all later server renders.
    quantityInput.removeAttribute('data-skip-value-update');
    this.normalizeValue();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    this.normalizeValue();
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}

